import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Ticket, TicketMessage, TicketMessageType, TicketPriority, CreateTicketMessageDTO, TicketAttachment } from '@/types/tickets';
import { ticketService } from '@/services/ticketService';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Send, Clock, User, Users, Building2, AlertCircle, CheckCircle2, XCircle, Copy, UserPlus, Trash2, Paperclip, FileText, Download, X, Pencil, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import ResolutionTimeCountdown from './ResolutionTimeCountdown';
import MultiUserSelect from '@/components/tickets/MultiUserSelect';
import { isAssignedToMe } from '@/lib/ticket-utils';

// Funções auxiliares para formatar datas de forma segura
const formatDateDistanceSafe = (dateString: string | undefined | null | Date): string => {
  if (!dateString) {
    return 'Data não disponível';
  }
  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }
    // Retornar data e hora exata no formato: "dd/MM/yyyy às HH:mm"
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch (error) {
    return 'Data inválida';
  }
};

const formatDateSafe = (dateString: string | undefined | null, formatStr: string): string | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return format(date, formatStr, { locale: ptBR });
  } catch {
    return null;
  }
};
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Renderiza um anexo conforme o tipo de mídia (imagem, vídeo ou documento)
const AttachmentView: React.FC<{ attachment: TicketAttachment }> = ({ attachment }) => {
  if (attachment.media_type === 'image') {
    return (
      <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={attachment.file_url}
          alt={attachment.file_name}
          className="max-h-48 rounded-md border object-contain hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }

  if (attachment.media_type === 'video') {
    return (
      <video src={attachment.file_url} controls className="max-h-64 rounded-md border w-full max-w-md" />
    );
  }

  // Documentos e demais tipos: link de download
  return (
    <a
      href={attachment.file_url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted transition-colors max-w-full"
    >
      <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <span className="truncate">{attachment.file_name}</span>
      <Download className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
    </a>
  );
};

// Lista de anexos de uma mensagem/ticket
const AttachmentList: React.FC<{ attachments?: TicketAttachment[] }> = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2">
      {attachments.map((att) => (
        <AttachmentView key={att.id} attachment={att} />
      ))}
    </div>
  );
};

interface TicketDetailsModalProps {
  ticket: Ticket | null;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  onDelete?: () => void; // Callback opcional para quando o ticket for deletado
  stages: any[];
  reasons: any[];
  users: any[];
}

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
  ticket,
  open,
  onClose,
  onUpdate,
  onDelete,
  stages,
  reasons,
  users
}) => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null); // texto após @ (null = inativo)
  const [mentionedIds, setMentionedIds] = useState<number[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<number | null>(null);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklistData, setChecklistData] = useState({
    feedback_sent_to_client: false,
    problem_recurred: false
  });
  const [finalObservations, setFinalObservations] = useState('');
  const [isSavingObservations, setIsSavingObservations] = useState(false);
  const [isAssigningToSelf, setIsAssigningToSelf] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showResolutionTimeField, setShowResolutionTimeField] = useState(false);
  const [resolutionTimeHours, setResolutionTimeHours] = useState<number | ''>('');
  const [isSavingResolutionTime, setIsSavingResolutionTime] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState<number | null>(null);
  const [localTicket, setLocalTicket] = useState<Ticket | null>(null); // Estado local para armazenar ticket completo carregado

  // Ref para rastrear se já marcou como visualizado nesta sessão
  const hasMarkedAsViewedRef = React.useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!ticket || !open) {
      // Limpar ref quando o modal fecha
      if (!open) {
        hasMarkedAsViewedRef.current.clear();
        setShowResolutionTimeField(false);
        setPendingStageChange(null);
        setResolutionTimeHours('');
        setLocalTicket(null);
      }
      return;
    }

    // Atualizar localTicket quando o ticket prop mudar
    setLocalTicket(ticket);

    console.log('[TicketDetailsModal] Ticket recebido:', ticket);
    
    // Marcar como visualizado apenas uma vez quando o modal abre (apenas se estiver na stage "Aberto")
    const isAssignedToUser = currentUser && ticket.assigned_to === currentUser.id;
    const isAbertoStage = ticket.stage?.name === 'Aberto';
    const isNotViewed = ticket.is_viewed !== true; // Tratar null, undefined e false como não visualizado
    
    console.log('[TicketDetailsModal] Verificando se deve marcar como visualizado:', {
      ticketId: ticket.id,
      isAssignedToUser,
      isAbertoStage,
      is_viewed: ticket.is_viewed,
      is_viewed_type: typeof ticket.is_viewed,
      isNotViewed,
      shouldMark: isAssignedToUser && isAbertoStage && isNotViewed
    });
    
    if (isAssignedToUser && isAbertoStage && isNotViewed) {
      const ticketId = ticket.id;
      if (!hasMarkedAsViewedRef.current.has(ticketId)) {
        hasMarkedAsViewedRef.current.add(ticketId);
        console.log('[TicketDetailsModal] Marcando ticket como visualizado:', ticketId);
        ticketService.markAsViewed(ticket.id)
          .then(() => {
            console.log('[TicketDetailsModal] Ticket marcado como visualizado com sucesso:', ticketId);
            // Atualizar o ticket localmente para refletir a mudança
            onUpdate();
          })
          .catch((error) => {
            console.error('[TicketDetailsModal] Erro ao marcar ticket como visualizado:', error);
            hasMarkedAsViewedRef.current.delete(ticketId); // Permite tentar novamente se falhar
          });
      }
    }
    
    loadTicketDetails();
    
    const stageName = ticket.stage?.name || '';
    if (stageName === 'Finalizado' || stageName === 'Concluído') {
      setShowChecklist(true);
      setChecklistData({
        feedback_sent_to_client: ticket.feedback_sent_to_client || false,
        problem_recurred: ticket.problem_recurred || false
      });
      setFinalObservations(ticket.final_observations || '');
    } else {
      setShowChecklist(false);
      setFinalObservations('');
    }

    // Verificar se precisa mostrar campo de previsão (ticket em "Em Análise" sem previsão)
    // Esta verificação será atualizada em loadTicketDetails quando os dados completos forem carregados
    const needsResolutionTime = stageName === 'Em Análise' && !ticket.resolution_time_hours && !showResolutionTimeField;
    if (needsResolutionTime) {
      setShowResolutionTimeField(true);
    }
  }, [ticket?.id, open, currentUser?.id, ticket?.stage?.name, ticket?.resolution_time_hours]); // Dependências mínimas para evitar recarregamentos

  const loadTicketDetails = async () => {
    if (!ticket) return;
    
    try {
      setIsLoading(true);
      const fullTicket = await ticketService.getTicket(ticket.id);
      console.log('[TicketDetailsModal] Ticket completo recebido:', fullTicket);
      console.log('[TicketDetailsModal] created_at do ticket:', (fullTicket as any).created_at || fullTicket.created_at);
      
      // Ordenar mensagens do mais recente para o mais antigo (já vem DESC do backend, mas garantir)
      const sortedMessages = (fullTicket.messages || []).sort((a, b) => {
        const dateA = new Date((a as any).created_at || (a as any).createdAt || a.created_at || 0);
        const dateB = new Date((b as any).created_at || (b as any).createdAt || b.created_at || 0);
        return dateB.getTime() - dateA.getTime();
      });
      setMessages(sortedMessages);
      
      // Atualizar ticket local com dados completos carregados
      setLocalTicket(fullTicket);
      
      // Atualizar observações finais se o ticket estiver em Finalizado
      if (fullTicket.stage?.name === 'Finalizado' || fullTicket.stage?.name === 'Concluído') {
        setFinalObservations(fullTicket.final_observations || '');
        setShowChecklist(true);
        setChecklistData({
          feedback_sent_to_client: fullTicket.feedback_sent_to_client || false,
          problem_recurred: fullTicket.problem_recurred || false
        });
      }
      
      // Verificar se precisa mostrar campo de previsão após carregar dados completos
      const stageName = fullTicket.stage?.name || '';
      const needsResolutionTime = stageName === 'Em Análise' && !fullTicket.resolution_time_hours && !showResolutionTimeField;
      if (needsResolutionTime) {
        setShowResolutionTimeField(true);
      } else if (stageName === 'Em Análise' && fullTicket.resolution_time_hours) {
        // Se tem previsão, garantir que o campo não seja mostrado
        setShowResolutionTimeField(false);
      }
      
      // Atualizar ticket localmente para refletir mudanças (como is_viewed)
      if (fullTicket.is_viewed !== undefined) {
        // Não chamar onUpdate aqui para evitar loop - será chamado apenas quando necessário
      }
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar detalhes do ticket',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!ticket) return;
    // É necessário ao menos texto ou um arquivo
    if (!newMessage.trim() && !selectedFile) return;

    try {
      setIsSendingMessage(true);

      if (selectedFile) {
        // Envia o anexo (e a legenda, se houver) como mensagem no chat
        await ticketService.uploadAttachment(ticket.id, selectedFile, {
          content: newMessage.trim() || undefined,
          asMessage: true
        });
      } else {
        // Mantém apenas ids cujo @Nome ainda aparece no texto (usuário pode ter apagado a menção).
        const idsValidos = mentionedIds.filter((id) => {
          const u = (users || []).find((x: any) => x.id === id);
          return u && newMessage.includes(`@${u.name}`);
        });
        const messageData: CreateTicketMessageDTO = {
          content: newMessage,
          message_type: TicketMessageType.COMMENT,
          mentioned_user_ids: idsValidos,
        };
        await ticketService.createMessage(ticket.id, messageData);
      }

      setNewMessage('');
      setMentionedIds([]);
      setMentionQuery(null);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadTicketDetails();
      onUpdate(); // Atualizar lista para refletir mudanças
      toast({
        title: selectedFile ? 'Anexo enviado' : 'Mensagem enviada',
        description: 'Adicionado ao histórico do ticket'
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.message || 'Erro ao enviar mensagem',
        variant: 'destructive'
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  const startEditMessage = (messageId: number, content: string) => {
    setEditingMessageId(messageId);
    setEditingContent(content);
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const handleSaveEditMessage = async (messageId: number) => {
    if (!ticket || !editingContent.trim()) return;
    try {
      setIsSavingEdit(true);
      await ticketService.updateMessage(ticket.id, messageId, editingContent.trim());
      cancelEditMessage();
      await loadTicketDetails();
      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Erro ao editar comentário',
        description: error?.response?.data?.error || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    if (!ticket) return;
    try {
      setIsDeletingMessage(true);
      await ticketService.deleteMessage(ticket.id, messageId);
      setDeletingMessageId(null);
      await loadTicketDetails();
      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Erro ao apagar comentário',
        description: error?.response?.data?.error || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingMessage(false);
    }
  };

  const handleUpdateChecklist = async () => {
    if (!ticket) return;

    try {
      setIsLoading(true);
      await ticketService.updateChecklist(ticket.id, checklistData);
      toast({
        title: 'Checklist atualizado',
        description: 'Checklist atualizado com sucesso'
      });
      onUpdate();
      await loadTicketDetails();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar checklist',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMoveStage = async (stageId: number) => {
    if (!ticket) return;

    // Verificar se está movendo de "Aberto" para "Em Análise"
    const oldStage = stages.find(s => s.id === ticket.stage_id);
    const newStage = stages.find(s => s.id === stageId);
    const isMovingToAnalysis = oldStage?.name === 'Aberto' && newStage?.name === 'Em Análise';

    // Se está movendo para "Em Análise", mostrar campo de previsão primeiro
    if (isMovingToAnalysis) {
      setPendingStageChange(stageId);
      setShowResolutionTimeField(true);
      return;
    }

    // Movimento normal para outras etapas
    try {
      setIsLoading(true);
      await ticketService.moveTicketStage(ticket.id, stageId);
      toast({
        title: 'Ticket movido',
        description: 'Ticket movido para nova etapa'
      });
      onUpdate();
      await loadTicketDetails();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Erro ao mover ticket',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveResolutionTime = async () => {
    if (!ticket || !pendingStageChange) return;

    if (!resolutionTimeHours || Number(resolutionTimeHours) < 1) {
      toast({
        title: 'Valor inválido',
        description: 'A previsão deve ser de pelo menos 1 hora',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsSavingResolutionTime(true);
      await ticketService.moveTicketStage(ticket.id, pendingStageChange, Number(resolutionTimeHours));
      toast({
        title: 'Sucesso',
        description: 'Ticket movido e previsão de tempo salva'
      });
      setShowResolutionTimeField(false);
      setPendingStageChange(null);
      setResolutionTimeHours('');
      onUpdate();
      await loadTicketDetails();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Erro ao salvar previsão de tempo',
        variant: 'destructive'
      });
    } finally {
      setIsSavingResolutionTime(false);
    }
  };

  const handleSaveFinalObservations = async () => {
    if (!ticket) return;

    try {
      setIsSavingObservations(true);
      await ticketService.updateTicket(ticket.id, {
        final_observations: finalObservations
      });
      toast({
        title: 'Observações salvas',
        description: 'Observações finais salvas com sucesso'
      });
      onUpdate();
      await loadTicketDetails();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: 'Erro ao salvar observações',
        variant: 'destructive'
      });
    } finally {
      setIsSavingObservations(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticket) return;

    // Confirmar exclusão
    if (!confirm(`Tem certeza que deseja excluir o ticket #${ticket.id}?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await ticketService.deleteTicket(ticket.id);
      
      toast({
        title: 'Ticket excluído',
        description: 'Ticket excluído com sucesso',
      });
      
      // Fechar o modal
      onClose();
      
      // Chamar callback de delete se fornecido, senão usar onUpdate
      if (onDelete) {
        onDelete();
      } else {
        onUpdate();
      }
    } catch (error: any) {
      console.error('Erro ao excluir ticket:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || error?.message || 'Erro ao excluir ticket',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopySummary = () => {
    if (!ticket) return;

    const formatDate = (dateString: string | undefined | null): string => {
      if (!dateString) return 'Data não disponível';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Data inválida';
        return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      } catch {
        return 'Data inválida';
      }
    };

    const clientName = ticket.client?.trade_name || ticket.client?.legal_name || 'Cliente não identificado';
    const reason = ticket.reason?.name || 'Motivo não informado';
    const title = ticket.title || 'Sem título';
    // Tentar obter created_at de diferentes formas possíveis
    const ticketDate = (ticket as any).created_at || ticket.created_at || (ticket as any).createdAt;
    const createdAt = formatDate(ticketDate);
    
    // Determinar próximas etapas baseado no estágio atual
    const currentStageName = ticket.stage?.name || '';
    let nextSteps = '';
    
    if (currentStageName === 'Aberto') {
      nextSteps = `📋 ETAPAS A SEGUIR PARA FINALIZAR:
1. Analisar o problema descrito
2. Atribuir para um operador responsável
3. Investigar e resolver o problema
4. Validar solução com o cliente
5. Finalizar o ticket`;
    } else if (currentStageName === 'Em Análise') {
      nextSteps = `📋 ETAPAS A SEGUIR PARA FINALIZAR:
1. Investigar e identificar a causa raiz
2. Desenvolver solução ou correção
3. Testar a solução
4. Validar com o cliente
5. Finalizar o ticket`;
    } else if (currentStageName === 'Em Desenvolvimento') {
      nextSteps = `📋 ETAPAS A SEGUIR PARA FINALIZAR:
1. Finalizar desenvolvimento/correção
2. Realizar testes completos
3. Aguardar validação do cliente
4. Finalizar o ticket`;
    } else if (currentStageName === 'Aguardando Cliente') {
      nextSteps = `📋 ETAPAS A SEGUIR PARA FINALIZAR:
1. Aguardar feedback do cliente
2. Aplicar ajustes se necessário
3. Confirmar resolução
4. Finalizar o ticket`;
    } else if (currentStageName === 'Finalizado') {
      nextSteps = `📋 ETAPAS A SEGUIR PARA CONCLUIR:
1. Realizar feedback com o cliente
2. Verificar se o problema voltou a acontecer
3. Registrar observações finais
4. Concluir o ticket`;
    } else {
      nextSteps = `📋 ETAPAS A SEGUIR PARA FINALIZAR:
1. Continuar com o processo de resolução
2. Validar solução com o cliente
3. Finalizar o ticket`;
    }

    let summary = `🎫 TICKET #${ticket.id}

👤 Cliente: ${clientName}
📋 Motivo: ${reason}
📝 Título: ${title}
📅 Data de Registro: ${createdAt}

${nextSteps}`;

    // Se estiver finalizado e tiver observações finais, incluir
    if (currentStageName === 'Finalizado' || currentStageName === 'Concluído') {
      if (ticket.final_observations) {
        summary += `\n\n📌 OBSERVAÇÕES FINAIS:\n${ticket.final_observations}`;
      }
      
      // Incluir resumo das mensagens importantes
      if (messages && messages.length > 0) {
        const importantMessages = messages.filter(m => 
          m.message_type === TicketMessageType.STATUS_CHANGE || 
          m.message_type === TicketMessageType.ASSIGNMENT ||
          m.content.length > 50
        );
        
        if (importantMessages.length > 0) {
          summary += `\n\n📋 HISTÓRICO DE MOVIMENTAÇÕES:`;
          importantMessages.forEach((msg, idx) => {
            const msgDate = formatDate(msg.created_at);
            const userName = msg.user?.name || 'Sistema';
            summary += `\n${idx + 1}. [${msgDate}] ${userName}: ${msg.content.substring(0, 100)}${msg.content.length > 100 ? '...' : ''}`;
          });
        }
      }
    }

    navigator.clipboard.writeText(summary);
    toast({
      title: 'Copiado!',
      description: 'Resumo do ticket copiado para a área de transferência.',
    });
  };

  // Detecta um @token sendo digitado (do último @ até o cursor, sem espaço).
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    const caret = e.target.selectionStart ?? value.length;
    const upToCaret = value.slice(0, caret);
    const match = upToCaret.match(/(?:^|\s)@([^\s@]*)$/);
    setMentionQuery(match ? match[1] : null);
  };

  const mentionCandidates = (mentionQuery !== null
    ? (users || []).filter((u: any) => {
        const q = mentionQuery.toLowerCase();
        return (u?.name || '').toLowerCase().includes(q);
      })
    : []
  ).slice(0, 6);

  // Insere a menção escolhida no texto e registra o id.
  const pickMention = (u: any) => {
    setNewMessage((prev) => prev.replace(/(^|\s)@([^\s@]*)$/, `$1@${u.name} `));
    setMentionedIds((ids) => (ids.includes(u.id) ? ids : [...ids, u.id]));
    setMentionQuery(null);
  };

  if (!ticket) return null;

  const PRIORITY_LABELS: Record<TicketPriority, string> = {
    [TicketPriority.LOW]: 'Baixa',
    [TicketPriority.MEDIUM]: 'Média',
    [TicketPriority.HIGH]: 'Alta',
    [TicketPriority.URGENT]: 'Urgente'
  };

  const getMessageTypeIcon = (type: TicketMessageType) => {
    switch (type) {
      case TicketMessageType.STATUS_CHANGE:
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case TicketMessageType.ASSIGNMENT:
        return <User className="h-4 w-4 text-purple-500" />;
      case TicketMessageType.SCHEDULED:
        return <Clock className="h-4 w-4 text-orange-500" />;
      case TicketMessageType.SYSTEM:
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      default:
        return null;
    }
  };

  // Função para lidar com o fechamento do modal
  const handleClose = (newOpen: boolean) => {
    if (!newOpen) {
      // Quando o modal está fechando, atualizar a lista
      onUpdate();
      onClose();
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-4xl h-[90vh] p-0 overflow-hidden"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center justify-between">
            <span>Ticket #{ticket.id}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopySummary}
                className="flex items-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Copiar Resumo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteTicket}
                disabled={isDeleting}
                className="flex items-center gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
                {isDeleting ? 'Excluindo...' : 'Excluir'}
              </Button>
              <Badge className={ticket.priority === TicketPriority.URGENT ? 'bg-red-500' : ''}>
                {PRIORITY_LABELS[ticket.priority]}
              </Badge>
            </div>
          </DialogTitle>
          <DialogDescription>{ticket.title}</DialogDescription>
        </DialogHeader>

        <div 
          className="flex-1 overflow-y-auto px-6 py-4"
          style={{ minHeight: 0 }}
        >
          <div className="flex flex-col gap-4">
          {/* Informações principais */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Cliente</Label>
              <div className="flex items-center gap-2 mt-1">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{ticket.client?.legal_name}</span>
              </div>
            </div>
            <div>
              <Label>Motivo</Label>
              <div className="mt-1">
                <Badge variant="outline">{ticket.reason?.name}</Badge>
              </div>
            </div>
            <div>
              <Label>Etapa</Label>
              <Select
                value={ticket.stage_id.toString()}
                onValueChange={(value) => handleMoveStage(Number(value))}
                disabled={isLoading || showResolutionTimeField}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stages.map(stage => (
                    <SelectItem key={stage.id} value={stage.id.toString()}>
                      {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Campo de previsão de tempo quando move para "Em Análise" */}
              {showResolutionTimeField && (
                <div className="mt-3 p-3 border rounded-md bg-yellow-50 border-yellow-200">
                  <Label className="text-sm font-semibold text-yellow-900">
                    Previsão de Tempo de Resolução (Obrigatório) *
                  </Label>
                  <p className="text-xs text-yellow-700 mb-2">
                    Defina quantas horas são necessárias para resolver este ticket
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="1"
                      placeholder="Ex: 24"
                      value={resolutionTimeHours}
                      onChange={(e) => setResolutionTimeHours(e.target.value ? Number(e.target.value) : '')}
                      className="flex-1"
                    />
                    <span className="self-center text-sm text-muted-foreground">horas</span>
                    <Button
                      onClick={handleSaveResolutionTime}
                      disabled={isSavingResolutionTime || !resolutionTimeHours || Number(resolutionTimeHours) < 1}
                      size="sm"
                    >
                      {isSavingResolutionTime ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </div>
              )}
              {/* Mostrar contagem regressiva se já tem previsão definida */}
              {(() => {
                // Usar localTicket se disponível (dados mais recentes), senão usar ticket prop
                const ticketToUse = localTicket || ticket;
                const isEmAnalise = ticketToUse?.stage?.name === 'Em Análise';
                const hasResolutionTime = ticketToUse?.resolution_time_hours && ticketToUse?.resolution_time_set_at;
                
                return isEmAnalise && hasResolutionTime ? (
                  <div className="mt-3">
                    <ResolutionTimeCountdown
                      resolutionTimeHours={ticketToUse.resolution_time_hours!}
                      resolutionTimeSetAt={ticketToUse.resolution_time_set_at!}
                      resolutionTimePausedAt={ticketToUse.resolution_time_paused_at}
                      ticketId={ticketToUse.id}
                      onRenew={async (newHours: number) => {
                        if (!ticket) return;
                        try {
                          const renewedTicket = await ticketService.renewResolutionTime(ticket.id, newHours);
                          setLocalTicket(renewedTicket);
                          onUpdate();
                          await loadTicketDetails();
                        } catch (error: any) {
                          throw error;
                        }
                      }}
                    />
                  </div>
                ) : null;
              })()}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Atribuído para</Label>
                {currentUser && !isAssignedToMe(ticket, currentUser.id) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!currentUser) return;
                      try {
                        setIsAssigningToSelf(true);
                        if (!ticket.assigned_to) {
                          await ticketService.updateTicket(ticket.id, {
                            assigned_to: currentUser.id
                          });
                        } else {
                          const existingCoIds = (ticket.coAssignees ?? []).map(a => a.id);
                          await ticketService.updateTicket(ticket.id, {
                            co_assignees: [...existingCoIds, currentUser.id],
                          });
                        }
                        toast({
                          title: 'Atribuído a você',
                        });
                        onUpdate?.();
                        await loadTicketDetails();
                      } catch (error) {
                        toast({
                          title: 'Erro',
                          description: 'Erro ao atribuir ticket',
                          variant: 'destructive'
                        });
                      } finally {
                        setIsAssigningToSelf(false);
                      }
                    }}
                    disabled={isAssigningToSelf}
                    className="h-7 text-xs"
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Atribuir para mim
                  </Button>
                )}
              </div>
              <Select
                value={ticket.assigned_to?.toString() || 'none'}
                onValueChange={async (value) => {
                  try {
                    const newAssignedTo = value === 'none' ? null : Number(value);
                    console.log('[TicketDetailsModal] Tentando atualizar atribuição:', {
                      ticketId: ticket.id,
                      value,
                      newAssignedTo,
                      newAssignedToType: typeof newAssignedTo,
                      newAssignedToIsNull: newAssignedTo === null,
                      currentAssignedTo: ticket.assigned_to
                    });

                    const updatePayload = {
                      assigned_to: newAssignedTo
                    };
                    
                    console.log('[TicketDetailsModal] Payload sendo enviado:', {
                      ...updatePayload,
                      assigned_to_type: typeof updatePayload.assigned_to,
                      assigned_to_is_null: updatePayload.assigned_to === null
                    });

                    const updatedTicket = await ticketService.updateTicket(ticket.id, updatePayload);
                    
                    console.log('[TicketDetailsModal] Ticket atualizado recebido:', {
                      id: updatedTicket.id,
                      assigned_to: updatedTicket.assigned_to,
                      assigned_to_type: typeof updatedTicket.assigned_to,
                      assigned_to_is_null: updatedTicket.assigned_to === null,
                      assigned_to_is_undefined: updatedTicket.assigned_to === undefined
                    });
                    
                    // Atualizar o ticket no componente pai
                    onUpdate();
                    // Recarregar detalhes para garantir sincronização
                    await loadTicketDetails();
                    
                    toast({
                      title: 'Sucesso',
                      description: newAssignedTo === null 
                        ? 'Atribuição removida com sucesso' 
                        : 'Atribuição atualizada com sucesso'
                    });
                  } catch (error: any) {
                    console.error('[TicketDetailsModal] Erro ao atualizar atribuição:', error);
                    toast({
                      title: 'Erro',
                      description: error?.response?.data?.error || 'Erro ao atualizar atribuição',
                      variant: 'destructive'
                    });
                  }
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Não atribuído" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não atribuído</SelectItem>
                  {users.length === 0 ? (
                    <SelectItem value="loading" disabled>Carregando usuários...</SelectItem>
                  ) : (
                    users.map(user => (
                      <SelectItem
                        key={user.id}
                        value={user.id.toString()}
                        className={currentUser && user.id === currentUser.id ? 'font-semibold bg-muted' : ''}
                      >
                        {user.name}
                        {currentUser && user.id === currentUser.id && ' (Você)'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" /> Co-responsáveis
              </Label>
              <MultiUserSelect
                users={users}
                selectedIds={(ticket.coAssignees ?? []).map(a => a.id)}
                excludedIds={ticket.assigned_to ? [ticket.assigned_to] : []}
                onChange={async (ids) => {
                  try {
                    await ticketService.updateTicket(ticket.id, { co_assignees: ids });
                    onUpdate?.();
                    await loadTicketDetails();
                    toast({ title: 'Co-responsáveis atualizados' });
                  } catch (error: any) {
                    toast({
                      title: 'Erro',
                      description: error?.response?.data?.error || 'Erro ao atualizar co-responsáveis',
                      variant: 'destructive'
                    });
                  }
                }}
                placeholder="Adicionar co-responsável"
              />
            </div>
          </div>

          <Separator />

          {/* Descrição */}
          <div>
            <Label>Descrição do Problema</Label>
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
              {ticket.description}
            </p>
            {/* Anexos enviados na criação do ticket */}
            <AttachmentList attachments={ticket.attachments} />
          </div>

          {/* Checklist (se na etapa Finalizado) */}
          {showChecklist && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Checklist de Finalização</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="feedback"
                        checked={checklistData.feedback_sent_to_client}
                        onCheckedChange={(checked) =>
                          setChecklistData({ ...checklistData, feedback_sent_to_client: !!checked })
                        }
                      />
                      <Label htmlFor="feedback" className="cursor-pointer">
                        Feedback realizado para o cliente?
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="recurred"
                        checked={checklistData.problem_recurred}
                        onCheckedChange={(checked) =>
                          setChecklistData({ ...checklistData, problem_recurred: !!checked })
                        }
                      />
                      <Label htmlFor="recurred" className="cursor-pointer">
                        Problema voltou a acontecer?
                      </Label>
                    </div>
                    <Button
                      onClick={handleUpdateChecklist}
                      disabled={isLoading}
                      className="mt-2"
                      size="sm"
                    >
                      Salvar Checklist
                    </Button>
                  </div>
                </div>

                {/* Observações Finais */}
                <Separator />
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Observações Finais</Label>
                  <p className="text-xs text-muted-foreground">
                    Registre aqui um resumo completo de tudo que foi feito no ticket. Esta informação será incluída no resumo copiável e enviada por e-mail ao cliente.
                  </p>
                  <Textarea
                    placeholder="Descreva todas as ações realizadas, soluções aplicadas, testes realizados e resultados obtidos..."
                    value={finalObservations}
                    onChange={(e) => setFinalObservations(e.target.value)}
                    rows={6}
                    className="mt-2"
                  />
                  <Button
                    onClick={handleSaveFinalObservations}
                    disabled={isSavingObservations || isLoading}
                    className="mt-2"
                    size="sm"
                  >
                    {isSavingObservations ? 'Salvando...' : 'Salvar Observações'}
                  </Button>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Chat interno */}
          <div className="flex flex-col">
            <Label className="mb-2">Histórico de Movimentações</Label>
            <ScrollArea className="h-[400px] border rounded-md p-4">
              <div className="space-y-4">
                {messages.map((message) => {
                  // Tentar obter created_at de diferentes formas possíveis (snake_case ou camelCase)
                  const messageDate = (message as any).created_at || (message as any).createdAt || message.created_at;
                  const canManage =
                    message.message_type === TicketMessageType.COMMENT &&
                    !!currentUser &&
                    message.user_id === currentUser.id;
                  const isEditing = editingMessageId === message.id;
                  return (
                    <div key={message.id} className="group flex gap-3">
                      <div className="flex-shrink-0">
                        {getMessageTypeIcon(message.message_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">
                            {message.user?.name || 'Sistema'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateDistanceSafe(messageDate)}
                          </span>
                          {message.updated_at && message.updated_at !== message.created_at && (
                            <span className="text-xs text-muted-foreground italic">(editado)</span>
                          )}
                          {canManage && !isEditing && (
                            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => startEditMessage(message.id, message.content)}
                                className="text-muted-foreground hover:text-foreground"
                                title="Editar comentário"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeletingMessageId(message.id)}
                                className="text-muted-foreground hover:text-destructive"
                                title="Apagar comentário"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              rows={3}
                              className="text-sm"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleSaveEditMessage(message.id)}
                                disabled={isSavingEdit || !editingContent.trim()}
                              >
                                <Check className="h-3.5 w-3.5 mr-1" />
                                {isSavingEdit ? 'Salvando...' : 'Salvar'}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEditMessage} disabled={isSavingEdit}>
                                <X className="h-3.5 w-3.5 mr-1" />
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {message.content}
                          </p>
                        )}
                        <AttachmentList attachments={message.attachments} />
                        {message.scheduled_for && (() => {
                          const formatted = formatDateSafe(message.scheduled_for, "dd/MM/yyyy 'às' HH:mm");
                          if (!formatted) return null;
                          return (
                            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Agendado para: {formatted}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    Nenhuma movimentação registrada ainda
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input de nova mensagem */}
            <div className="mt-4 space-y-2">
              {/* Preview do arquivo selecionado */}
              {selectedFile && (
                <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  <Paperclip className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <span className="truncate flex-1">{selectedFile.name}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remover anexo"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setSelectedFile(file);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSendingMessage}
                  title="Anexar mídia"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                <div className="relative flex-1">
                  <Textarea
                    placeholder={selectedFile ? 'Legenda (opcional)...' : 'Adicionar observação ou comentário... (@ para mencionar)'}
                    value={newMessage}
                    onChange={handleMessageChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && mentionQuery !== null) {
                        e.preventDefault();
                        setMentionQuery(null);
                        return;
                      }
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleSendMessage();
                      }
                    }}
                    className="w-full"
                    rows={2}
                  />
                  {mentionQuery !== null && mentionCandidates.length > 0 && (
                    <div className="absolute bottom-full left-0 mb-1 z-50 w-64 max-h-48 overflow-auto rounded-md border bg-popover shadow-md">
                      {mentionCandidates.map((u: any) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => pickMention(u)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                        >
                          {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && !selectedFile) || isSendingMessage}
                  size="icon"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
      <AlertDialog open={deletingMessageId !== null} onOpenChange={(open) => !open && setDeletingMessageId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar comentário?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Anexos deste comentário também serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingMessageId !== null && handleDeleteMessage(deletingMessageId)}
              disabled={isDeletingMessage}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingMessage ? 'Apagando...' : 'Apagar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TicketDetailsModal;

